import { formatMeasureValue } from '../lib/visualContract'
import { getMeasureMetadata } from '../lib/analyticalQuery'

/**
 * Combo — same TimeSeriesResult input as Line, but renders the first
 * yAxis measure as bars and the rest as lines on a shared time axis.
 * Reuses the exact same data-reading pattern as Line (series[i].rows[0]
 * for project grain) — no separate data logic, only a different visual
 * treatment per measure.
 *
 * Requires at least 2 yAxis entries (validated by visualContract.js's
 * validateVisualDefinition() before this component is ever reached).
 */
export default function Combo({ result, yAxis }) {
  const series = result?.series || []

  if (series.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>No data available</div>
  }

  const [barMeasure, ...lineMeasures] = yAxis
  const COLORS = ['#1B3A6B', '#1A7A4A', '#B06D00', '#8B5CF6']

  const chartWidth = 600
  const chartHeight = 220
  const xStep = series.length > 1 ? chartWidth / series.length : chartWidth
  const barWidth = xStep * 0.5

  const barValues = series.map(p => p.rows?.[0]?.values?.[barMeasure] ?? 0)
  const lineValuesByMeasure = lineMeasures.map(m => series.map(p => p.rows?.[0]?.values?.[m] ?? 0))
  const allValues = [...barValues, ...lineValuesByMeasure.flat()]
  const maxValue = Math.max(...allValues, 1)
  const minValue = Math.min(...allValues, 0)
  const range = maxValue - minValue || 1
  const yFor = (v) => chartHeight - ((v - minValue) / range) * chartHeight

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#E5E7EB' }} />
          {getMeasureMetadata(barMeasure).label}
        </div>
        {lineMeasures.map((m, i) => (
          <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
            {getMeasureMetadata(m).label}
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ width: '100%', height: 220 }}>
        {barValues.map((v, i) => (
          <rect
            key={i}
            x={i * xStep + (xStep - barWidth) / 2}
            y={yFor(v)}
            width={barWidth}
            height={chartHeight - yFor(v)}
            fill="#E5E7EB"
          />
        ))}
        {lineValuesByMeasure.map((values, mi) => (
          <g key={mi}>
            <polyline
              fill="none"
              stroke={COLORS[mi % COLORS.length]}
              strokeWidth={2}
              points={values.map((v, i) => `${i * xStep + xStep / 2},${yFor(v)}`).join(' ')}
            />
            {values.map((v, i) => (
              <circle
                key={i}
                cx={i * xStep + xStep / 2}
                cy={yFor(v)}
                r={4}
                fill={series[i].hasSnapshot === false ? '#fff' : COLORS[mi % COLORS.length]}
                stroke={COLORS[mi % COLORS.length]}
                strokeWidth={2}
              />
            ))}
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
        {series.map(point => <span key={point.time}>{point.time}</span>)}
      </div>
    </div>
  )
}
